import { MouseEvent, useRef, useState } from 'react';
import styled from 'styled-components';
import { Flex } from '@elementary/common-ui/components';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { Tooltip, Typography, useTheme } from '@mui/material';
import { Handle, NodeProps, Position } from 'reactflow';
import {
  GRAPH_NODE_HEIGHT,
  LINEAGE_COMPACT_HIGHLIGHT_SCALE,
  LINEAGE_DOT_PATH_RING_WIDTH,
  LINEAGE_PILL_HEIGHT,
  LINEAGE_PILL_MIN_WIDTH,
} from './constants';
import { CompactLineageZoomTier, ReactFlowNodeData } from './types';
import { useLineageGraphContext } from './LineageGraphContext';
import { CompactExpandButton } from './GraphNodeCompactParts';
import NodeContextMenu from './NodeContextMenu';
import useLineageNodeColors from './hooks/useLineageNodeColors';
import useNodeCriticalAsset from './hooks/useNodeCriticalAsset';
import {
  getFilterHighlightCss,
  getSelectedShadow,
  isFilterMatch,
} from './utils/lineageDisplaySettings';
import {
  getGraphNodeBorderColor,
  getGraphNodeChildrenState,
  getLineageDotDiameter,
} from './utils/lineageGraphUtils';

// The footprint keeps the card's size so React Flow's culling and fit view see
// the same bounds as in card form, but ignores the pointer so only the
// pill/dot itself is clickable and draggable.
const Footprint = styled.div<{ $width: number }>`
  position: relative;
  width: ${({ $width }) => $width}px;
  height: ${GRAPH_NODE_HEIGHT}px;
  pointer-events: none;
`;

// Centered on the same Y offset the card anchors its edges at, so edges don't
// jump when switching between card, pill and dot.
const Anchor = styled(Flex)`
  position: absolute;
  top: ${GRAPH_NODE_HEIGHT / 2}px;
  left: 0;
  transform: translateY(-50%);
  align-items: center;
  pointer-events: auto;
`;

const Dot = styled.div<{
  $diameter: number;
  $color: string;
  $isHighlighted: boolean;
  $isFilterMatch: boolean;
  $selectedShadow?: string;
}>`
  width: ${({ $diameter }) => $diameter}px;
  height: ${({ $diameter }) => $diameter}px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  box-shadow: ${({ $isHighlighted, $selectedShadow, theme }) =>
    [
      $isHighlighted
        ? `0 0 0 ${LINEAGE_DOT_PATH_RING_WIDTH}px ${theme.palette.common.blue7}`
        : `0 1px 3px ${theme.palette.common.gray11}`,
      $selectedShadow,
    ]
      .filter(Boolean)
      .join(', ')};
  transition: width 0.15s ease, height 0.15s ease, background 0.15s ease;

  ${({ theme, $isFilterMatch, $isHighlighted }) =>
    $isFilterMatch &&
    getFilterHighlightCss(theme.palette, {
      scale: LINEAGE_COMPACT_HIGHLIGHT_SCALE,
      innerRingWidth: $isHighlighted ? LINEAGE_DOT_PATH_RING_WIDTH : 0,
    })}
`;

const Pill = styled(Flex)<{
  $borderColor: string;
  $background: string;
  $maxWidth: number;
  $expandedWidth?: number;
  $isFilterMatch: boolean;
  $selectedShadow?: string;
}>`
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  height: ${LINEAGE_PILL_HEIGHT}px;
  padding: 0 20px;
  background: ${({ $background }) => $background};
  border: 3px solid ${({ $borderColor }) => $borderColor};
  ${({ $expandedWidth, $maxWidth, theme }) =>
    $expandedWidth
      ? `
    width: ${$expandedWidth}px;
    border-radius: 20px 20px 0 0;
  `
      : `
    max-width: ${$maxWidth}px;
    border-radius: 999px;
    box-shadow: 0 2px 6px ${theme.palette.common.gray11};
  `}

  ${({ theme, $isFilterMatch }) =>
    $isFilterMatch &&
    getFilterHighlightCss(theme.palette, {
      scale: LINEAGE_COMPACT_HIGHLIGHT_SCALE,
    })}
  ${({ $selectedShadow }) =>
    $selectedShadow && `box-shadow: ${$selectedShadow};`}
`;

const PILL_TOP = (GRAPH_NODE_HEIGHT - LINEAGE_PILL_HEIGHT) / 2;

// An expanded pill's rows are separate React Flow nodes, so the filter ring and
// selected shadow are drawn on an outline spanning the pill and all its rows.
const PanelOutline = styled.div<{
  $height: number;
  $isFilterMatch: boolean;
  $selectedShadow?: string;
}>`
  position: absolute;
  top: ${PILL_TOP}px;
  left: 0;
  width: 100%;
  height: ${({ $height }) => $height}px;
  border-radius: 20px;
  pointer-events: none;

  ${({ theme, $isFilterMatch }) =>
    $isFilterMatch &&
    getFilterHighlightCss(theme.palette, {
      scale: LINEAGE_COMPACT_HIGHLIGHT_SCALE,
    })}
  ${({ $selectedShadow }) =>
    $selectedShadow && `box-shadow: ${$selectedShadow};`}
`;

const PillLabel = styled(Typography)<{ $isSelected: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 26px;
  font-weight: ${({ $isSelected }) => ($isSelected ? 700 : 500)};
  line-height: normal;
  color: ${({ theme }) => theme.palette.common.black};
`;

const AnchorHandle = styled(Handle)`
  opacity: 0;
  border: none;
  width: 1px;
  height: 1px;
  min-width: 1px;
  min-height: 1px;
  max-height: 1px;
  background: transparent;

  &.react-flow__handle-left {
    left: 0;
    top: 50%;
  }

  &.react-flow__handle-right {
    left: auto;
    right: 0;
    top: 50%;
  }
`;

type MenuProps = {
  node: NodeProps<ReactFlowNodeData>;
  pos: { left: number; top: number };
  onClose: () => void;
  onHighlightNodeClick: () => void;
};

export const GraphNodeCompactMenu = ({
  node,
  pos,
  onClose,
  onHighlightNodeClick,
}: MenuProps) => {
  const hasPermission = useEnvPermission();
  const { isCriticalAsset, toggleCriticalAsset, hasSetPermissions } =
    useNodeCriticalAsset(node.id);

  return (
    <NodeContextMenu
      hideMenuButton
      showMenuButton={false}
      node={node}
      pos={pos}
      onClose={onClose}
      onHighlightNodeClick={onHighlightNodeClick}
      isCriticalAsset={isCriticalAsset}
      onSetIsCriticalAssets={toggleCriticalAsset}
      showCriticalAssetOption={hasSetPermissions}
      showTestResults={hasPermission('VIEW_TESTS')}
    />
  );
};

type Props = NodeProps<ReactFlowNodeData> & {
  tier: CompactLineageZoomTier;
};

const GraphNodeCompact = ({ tier, ...node }: Props) => {
  const { id, data } = node;
  const theme = useTheme();
  const { highlightedNode, isControlled, nodeDegrees } =
    useLineageGraphContext();
  const footprintRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const handleMenuOpen = (e: MouseEvent) => {
    if (isControlled) return;
    e.stopPropagation();
    e.preventDefault();
    setMenuPos({ left: e.clientX, top: e.clientY });
  };

  const isHighlighted = id === highlightedNode?.id || !!data.isOnHighlightPath;
  const label = data.metadata.label ?? '';
  const { isExpandable, showChildrenSection, isVisuallyExpanded } =
    getGraphNodeChildrenState(data);

  const isRestricted = data.metadata.isRestricted === true;
  const nodeColors = useLineageNodeColors(data);
  const color = getGraphNodeBorderColor(theme.palette, {
    isHighlighted,
    isRestricted,
    colorByBorderColor: nodeColors.border,
  });
  const dotColor = getGraphNodeBorderColor(theme.palette, {
    isRestricted,
    colorByBorderColor: nodeColors.border,
  });
  const isFiltered = isFilterMatch(data);
  const isSelected = id === highlightedNode?.id;
  const selectedShadow = isSelected
    ? getSelectedShadow(theme.palette, LINEAGE_COMPACT_HIGHLIGHT_SCALE)
    : undefined;
  const isExpandedPill =
    tier === 'pill' && !!data.isExpanded && !!data.children?.length;
  const hasPanelOutline =
    isExpandedPill && data.compactPanelBottom !== undefined;

  return (
    <Footprint ref={footprintRef} $width={data.width}>
      {hasPanelOutline && (
        <PanelOutline
          $height={(data.compactPanelBottom ?? 0) - PILL_TOP}
          $isFilterMatch={isFiltered}
          $selectedShadow={selectedShadow}
        />
      )}
      <Tooltip title={label} placement="top" disableInteractive>
        <Anchor onContextMenu={handleMenuOpen}>
          <AnchorHandle type="target" position={Position.Left} />
          {tier === 'pill' ? (
            <Pill
              $borderColor={color}
              $background={
                isRestricted
                  ? theme.palette.common.gray3
                  : nodeColors.background
              }
              $isFilterMatch={isFiltered && !hasPanelOutline}
              $selectedShadow={hasPanelOutline ? undefined : selectedShadow}
              $maxWidth={Math.max(data.width, LINEAGE_PILL_MIN_WIDTH)}
              $expandedWidth={isExpandedPill ? data.width : undefined}
            >
              <PillLabel noWrap $isSelected={isSelected}>
                {label}
              </PillLabel>
              {showChildrenSection && isExpandable && (
                <CompactExpandButton
                  size={28}
                  isLoading={!!data.isColumnsLoading}
                  isExpanded={isVisuallyExpanded}
                />
              )}
            </Pill>
          ) : (
            <Dot
              $diameter={getLineageDotDiameter(
                nodeDegrees.byNodeId[id] ?? 0,
                nodeDegrees.max,
              )}
              $color={dotColor}
              $isHighlighted={isHighlighted}
              $isFilterMatch={isFiltered}
              $selectedShadow={selectedShadow}
            />
          )}
          <AnchorHandle type="source" position={Position.Right} />
        </Anchor>
      </Tooltip>

      {menuPos && (
        <GraphNodeCompactMenu
          node={node}
          pos={menuPos}
          onClose={() => setMenuPos(null)}
          onHighlightNodeClick={() => footprintRef.current?.click()}
        />
      )}
    </Footprint>
  );
};

export default GraphNodeCompact;
